/**
 * Status Badge Component
 * Displays normalized status with appropriate colors
 */

import type { StatusInfo } from '../api/subscribers.api';

interface StatusBadgeProps {
  status: StatusInfo;
  showDescription?: boolean;
  className?: string;
}

const statusConfig: Record<
  StatusInfo['normalized'],
  { label: string; bgColor: string; textColor: string; borderColor: string; icon: string }
> = {
  eligible: {
    label: 'Eligible',
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    borderColor: 'border-green-200',
    icon: '●',
  },
  stopped: {
    label: 'Stopped',
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
    icon: '✕',
  },
  unknown: {
    label: 'Unknown',
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-700',
    borderColor: 'border-yellow-200',
    icon: '?',
  },
};

export function StatusBadge({ status, showDescription = false, className = '' }: StatusBadgeProps) {
  const config = statusConfig[status.normalized];

  return (
    <div className={`inline-flex flex-col ${className}`}>
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.bgColor} ${config.textColor} ${config.borderColor}`}
        title={`CRM Status: ${status.raw || 'N/A'}`}
      >
        <span className="text-[10px]">{config.icon}</span>
        <span>{config.label}</span>
      </span>
      {showDescription && status.description && (
        <span className="text-xs text-gray-500 mt-0.5 ml-1">{status.description}</span>
      )}
    </div>
  );
}

/**
 * Small status indicator for table cells
 */
export function StatusIndicator({ status }: { status: StatusInfo }) {
  const config = statusConfig[status.normalized];

  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${config.textColor}`}
      title={`CRM Status: ${status.raw || 'N/A'}`}
    >
      <span className="text-[10px]">{config.icon}</span>
      <span>{config.label}</span>
    </span>
  );
}

/**
 * Extended badge with raw status
 */
export function StatusBadgeWithRaw({ status }: { status: StatusInfo }) {
  return (
    <div className="flex flex-col gap-0.5">
      <StatusBadge status={status} />
      {status.raw && status.raw !== status.normalized && (
        <span className="text-xs text-gray-400">CRM: {status.raw}</span>
      )}
    </div>
  );
}
